import React, { useEffect, useRef } from 'react';
import { useTheme } from '../../contexts/ThemeContext';
import { useVariant } from '../../contexts/VariantContext';
import { useDomainManager } from '../../config';
import { getDomainImageAuto, handleImageErrorWithFallback } from '../../utils/imageManager';
import { renderCtaButton } from '../../utils/ctaHelper';
import { usePageTitle } from '../../hooks/usePageTitle';

const DarkVariant = ({ heroContent, brand }) => {
  const mainImageRef = useRef(null);
  const secondaryImageRef = useRef(null);
  const { cta, domainConfig } = useTheme();
  const { styles } = useVariant();
  const domainManager = useDomainManager(brand?.id);
  
  // Get hostname-specific H1 title
  const h1Title = usePageTitle('home');

  // Get domain-specific features content using domain manager
  const getDomainFeatures = () => {
    const features = domainManager.getFeatures();
    if (features) {
      return {
        title: features.title,
        subtitle: features.subtitle,
        description: features.description,
        features: features.items
      };
    }
    
    // Fallback for domains without features configured
    return {
      title: 'Advanced Features',
      subtitle: 'Cutting-edge solutions in a sleek dark interface',
      description: 'Next-generation technology designed to revolutionize your business operations with intelligent automation and dark mode optimization.',
      features: [
        {
          title: 'Smart Analytics',
          description: 'Advanced data processing and insights with real-time monitoring capabilities.',
          image: getDomainImageAuto(brand.id, 'feature-1', ['jpg', 'jpeg', 'png', 'gif', 'svg', 'webp']),
          icon: '📊'
        },
        {
          title: 'Secure Platform',
          description: 'Enterprise-grade security with end-to-end encryption and compliance standards.',
          image: getDomainImageAuto(brand.id, 'feature-2', ['jpg', 'jpeg', 'png', 'gif', 'svg', 'webp']),
          icon: '🛡️'
        },
        {
          title: '24/7 Support',
          description: 'Round-the-clock support with dedicated customer service and technical assistance.',
          image: getDomainImageAuto(brand.id, 'feature-3', ['jpg', 'jpeg', 'png', 'gif', 'svg', 'webp']),
          icon: '🆘'
        }
      ]
    };
  };

  // Get domain-specific testimonials content using domain manager
  const getDomainTestimonials = () => {
    const testimonials = domainManager.getTestimonials();
    if (testimonials && testimonials.length > 0) {
      return testimonials;
    }
    
    // Fallback for domains without testimonials configured
    return [
      {
        content: "The dark theme interface is absolutely stunning. It's easy on the eyes and looks incredibly professional.",
        author: "Alex Chen",
        role: "Senior Developer",
        company: "TechCorp",
        avatar: null
      },
      {
        content: "This solution has transformed our workflow. The dark mode makes it perfect for late-night coding sessions.",
        author: "Sarah Johnson",
        role: "Product Manager",
        company: "InnovateLab",
        avatar: null
      }
    ];
  };

  const domainFeatures = getDomainFeatures();
  const domainTestimonials = getDomainTestimonials();

  // Reset image elements when domain changes
  useEffect(() => {
    if (mainImageRef.current) {
      Object.keys(mainImageRef.current.dataset).forEach(key => {
        if (key.startsWith('triedExtensions_')) {
          delete mainImageRef.current.dataset[key];
        }
      });
    }
    if (secondaryImageRef.current) {
      Object.keys(secondaryImageRef.current.dataset).forEach(key => {
        if (key.startsWith('triedExtensions_')) {
          delete secondaryImageRef.current.dataset[key];
        }
      });
    }
  }, [brand.id]);

  const { mainImage, secondaryImage, title, subtitle, features } = heroContent;

  return (
    <div className="min-h-screen bg-gray-900 text-white">
      {/* Hero Section - Dark Theme Design */}
      <section className="relative pt-20 pb-32 bg-gradient-to-br from-gray-900 via-gray-800 to-black overflow-hidden">
        {/* Dark Background Elements */}
        <div className="absolute inset-0">
          <div className="absolute top-20 left-20 w-72 h-72 bg-blue-500/10 rounded-full blur-3xl"></div>
          <div className="absolute bottom-20 right-20 w-96 h-96 bg-purple-500/10 rounded-full blur-3xl"></div>
          <div className="absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 w-64 h-64 bg-cyan-500/5 rounded-full blur-3xl"></div>
        </div>

        <div className="relative max-w-7xl mx-auto px-6">
          <div className="grid lg:grid-cols-2 gap-16 items-center">
            {/* Left Column - Content */}
            <div className="text-white">
              <div className="mb-6">
                <span className="inline-block px-4 py-2 text-sm font-semibold bg-white/10 backdrop-blur-sm rounded-full text-white border border-white/20">
                  {brand.name}
                </span>
              </div>
              
              <h1 className="text-5xl lg:text-6xl font-bold mb-6 leading-tight bg-gradient-to-r from-white via-blue-200 to-purple-200 bg-clip-text text-transparent">
                {h1Title || title}
              </h1>
              
              <p className="text-xl text-gray-300 mb-8 leading-relaxed">
                {subtitle}
              </p>
              
              {/* Features - Dark Style */}
              <div className="space-y-4 mb-8">
                {features.map((feature, index) => (
                  <div key={index} className="flex items-center space-x-3 p-4 bg-white/5 backdrop-blur-sm rounded-xl border border-white/10">
                    <div className="w-2 h-2 bg-gradient-to-r from-blue-400 to-purple-400 rounded-full"></div>
                    <span className="text-white font-medium">{feature}</span>
                  </div>
                ))}
              </div>
              
              {/* CTA Buttons - Dark Style */}
              <div className="flex flex-col sm:flex-row gap-4">
                {renderCtaButton(cta?.primary, 'Request Demo', 'bg-gradient-to-r from-blue-600 to-purple-600 text-white px-8 py-4 rounded-xl font-bold hover:from-blue-700 hover:to-purple-700 transition-all duration-300 shadow-lg hover:shadow-xl transform hover:-translate-y-1 text-center', domainConfig?.shopLink)}
                {renderCtaButton(cta?.secondary, 'Learn More', 'border-2 border-white/30 text-white px-8 py-4 rounded-xl font-bold hover:bg-white/10 transition-all duration-300 backdrop-blur-sm text-center', domainConfig?.shopLink)}
              </div>
            </div>
            
            {/* Right Column - Images */}
            <div className="relative">
              {/* Main Image with Dark Glow */}
              <div className="relative group">
                <div className="absolute -inset-1 bg-gradient-to-r from-blue-600 to-purple-600 rounded-2xl blur opacity-75 group-hover:opacity-100 transition duration-1000 group-hover:duration-200"></div>
                <div className="relative">
                  <img 
                    ref={mainImageRef}
                    src={mainImage} 
                    alt={title}
                    className="w-full aspect-video object-cover rounded-2xl"
                    onError={(e) => handleImageErrorWithFallback(e, brand.id, 'hero-main')}
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-black/40 to-transparent rounded-2xl"></div>
                </div>
              </div>
              
              {/* Secondary Image - Floating with Dark Theme */}
              <div className="absolute -bottom-8 -left-8 w-48 h-32 animate-float">
                <div className="relative group">
                  <div className="absolute -inset-1 bg-gradient-to-r from-cyan-600 to-blue-600 rounded-xl blur opacity-50 group-hover:opacity-75 transition duration-1000"></div>
                  <div className="relative">
                    <img 
                      ref={secondaryImageRef}
                      src={secondaryImage} 
                      alt={`${title} - Secondary`}
                      className="w-full h-full object-cover rounded-xl shadow-2xl"
                      onError={(e) => handleImageErrorWithFallback(e, brand.id, 'hero-secondary')}
                    />
                    <div className="absolute inset-0 bg-gradient-to-t from-black/30 to-transparent rounded-xl"></div>
                  </div>
                </div>
              </div>

              {/* Dark Theme Elements */}
              <div className="absolute top-4 right-4 w-16 h-16 bg-white/10 backdrop-blur-md rounded-full flex items-center justify-center border border-white/20">
                <div className="w-8 h-8 bg-gradient-to-r from-blue-400 to-purple-400 rounded-full flex items-center justify-center">
                  <div className="w-4 h-4 bg-white rounded-full"></div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Award Section - Dark Theme */}
      <section className="py-20 bg-gray-800/50">
        <div className="max-w-7xl mx-auto px-6">
          <div className="text-center mb-12">
            <h2 className="text-3xl font-bold text-white mb-4">Recognition & Performance</h2>
            <p className="text-lg text-gray-300">Trusted by industry leaders and recognized for excellence</p>
          </div>

          <div className="grid grid-cols-2 md:grid-cols-4 gap-8">
            {[
              { number: '1000+', label: 'Active Customers', color: 'from-blue-500 to-cyan-500' },
              { number: '99.9%', label: 'Uptime', color: 'from-green-500 to-emerald-500' },
              { number: '24/7', label: 'Support', color: 'from-purple-500 to-pink-500' },
              { number: '5★', label: 'Rating', color: 'from-yellow-500 to-orange-500' }
            ].map((stat, index) => (
              <div key={index} className="text-center p-4 bg-white/5 backdrop-blur-md rounded-2xl border border-white/10 hover:bg-white/10 transition-all duration-300">
                <div className={`text-4xl font-bold mb-2 bg-gradient-to-r ${stat.color} bg-clip-text text-transparent`}>
                  {stat.number}
                </div>
                <div className="text-gray-300 font-medium">{stat.label}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Features Section - Dark Theme Cards */}
      <section className="py-20 bg-gray-900">
        <div className="max-w-7xl mx-auto px-6">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold text-white mb-4">{domainFeatures.title}</h2>
            <p className="text-xl text-gray-300">{domainFeatures.subtitle}</p>
            {domainFeatures.description && (
              <p className="text-base text-gray-400 max-w-3xl mx-auto mt-4">{domainFeatures.description}</p>
            )}
          </div>

          <div className="grid md:grid-cols-3 gap-8">
            {domainFeatures.features.map((feature, index) => (
              <div key={index} className="group p-8 bg-white/5 backdrop-blur-md rounded-2xl border border-white/10 hover:bg-white/10 transition-all duration-300 hover:scale-105">
                <div className="w-16 h-16 bg-gradient-to-r from-blue-500 to-purple-500 rounded-2xl mx-auto mb-6 flex items-center justify-center group-hover:scale-110 transition-transform duration-300">
                  <div className="text-2xl">{feature.icon}</div>
                </div>
                <h3 className="text-xl font-bold text-white mb-4 text-center">{feature.title}</h3>
                <p className="text-gray-300 text-center leading-relaxed">
                  {feature.description}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Testimonials Section - Dark Theme */}
      <section className="py-20 bg-gray-800/50">
        <div className="max-w-7xl mx-auto px-6">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold text-white mb-4">What Our Users Say</h2>
            <p className="text-xl text-gray-300">Trusted by professionals worldwide</p>
          </div>
          
          <div className="grid md:grid-cols-2 gap-8">
            {domainTestimonials.map((testimonial, index) => (
              <div key={index} className="bg-white/5 backdrop-blur-md rounded-2xl border border-white/10 p-8">
                <div className="flex items-center justify-between mb-6">
                  <div className="flex items-center space-x-4">
                    {testimonial.avatar ? (
                      <img 
                        src={testimonial.avatar} 
                        alt={testimonial.author}
                        className="w-12 h-12 rounded-full object-cover"
                        onError={(e) => {
                          e.target.style.display = 'none';
                          e.target.nextSibling.style.display = 'flex';
                        }}
                      />
                    ) : null}
                    <div className={`w-12 h-12 bg-gradient-to-r from-blue-500 to-purple-500 rounded-full flex items-center justify-center ${testimonial.avatar ? 'hidden' : ''}`}>
                      <span className="text-white font-bold text-lg">
                        {testimonial.author.split(' ').map(n => n[0]).join('')}
                      </span>
                    </div>
                    <div>
                      <p className="text-lg font-semibold text-white">
                        {testimonial.author}
                      </p>
                      <p className="text-sm text-gray-400">
                        {testimonial.role}, {testimonial.company}
                      </p>
                    </div>
                  </div>
                  <div className="text-blue-400">
                    <svg className="h-8 w-8" fill="currentColor" viewBox="0 0 24 24">
                      <path d="M14.017 21v-7.391C14.017 10.257 16.274 8 19.625 8 20.937 8 22 9.063 22 10.375v1.25C22 12.937 20.937 14 19.625 14h-1.25v7h-4.358z" />
                      <path d="M3.5 21v-7.391C3.5 10.257 5.757 8 9.108 8 10.42 8 11.483 9.063 11.483 10.375v1.25C11.483 12.937 10.42 14 9.108 14h-1.25v7H3.5z" />
                    </svg>
                  </div>
                </div>
                <p className="text-gray-300 leading-relaxed">{testimonial.content}</p>
                <div className="mt-6 flex">
                  {[1, 2, 3, 4, 5].map((star) => (
                    <svg key={star} className="h-5 w-5 text-yellow-400" fill="currentColor" viewBox="0 0 20 20">
                      <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z" />
                    </svg>
                  ))}
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section - Dark Theme */}
      <section className="py-20 bg-gradient-to-r from-gray-800 to-gray-900">
        <div className="max-w-4xl mx-auto px-6 text-center">
          <h2 className="text-4xl font-bold text-white mb-4">Ready to Get Started?</h2>
          <p className="text-xl text-gray-300 mb-8">
            Join thousands of businesses that trust our solution to grow their operations.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            {renderCtaButton(cta?.primary, 'Request Demo', 'px-8 py-4 bg-gradient-to-r from-blue-600 to-purple-600 text-white font-bold rounded-lg hover:from-blue-700 hover:to-purple-700 transition-all duration-300 shadow-lg hover:shadow-xl', domainConfig?.shopLink)}
            {renderCtaButton(cta?.secondary, 'Learn More', 'px-8 py-4 border-2 border-white/30 text-white font-bold rounded-lg hover:bg-white/10 transition-all duration-300', domainConfig?.shopLink)}
          </div>
        </div>
      </section>

      <style jsx>{`
        @keyframes float {
          0%, 100% { transform: translateY(0px); }
          50% { transform: translateY(-20px); }
        }
        .animate-float {
          animation: float 6s ease-in-out infinite;
        }
      `}</style>
    </div>
  );
};

export default DarkVariant;
